// src/contexts/GuestContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";

interface Guest {
  name: string;
  invited: number;
  confirmed?: number;
  hasConfirmed?: boolean;
  [key: string]: any;
}

interface GuestContextType {
  token: string | null;
  guest: Guest | null;
  isGuestLoaded: boolean;
  confirmAttendance: (confirmed: number) => Promise<void>;
}

const GuestContext = createContext<GuestContextType>({
  token: null,
  guest: null,
  isGuestLoaded: false,
  confirmAttendance: async () => {},
});

export const useGuest = () => useContext(GuestContext);

export const GuestProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(null);
  const [guest, setGuest] = useState<Guest | null>(null);
  const [isGuestLoaded, setIsGuestLoaded] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("invite");

    // const finalToken = tokenFromUrl || localStorage.getItem("guestToken");
    const finalToken = tokenFromUrl;
    if (finalToken) {
      setToken(finalToken);
      // localStorage.setItem("guestToken", finalToken);
      validateGuest(finalToken);
    } else {
      setIsGuestLoaded(true);
    }
  }, []);

  const validateGuest = async (token: string) => {
    try {
      const docRef = doc(db, "guests", token);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setGuest(docSnap.data() as Guest);
      } else {
        setGuest(null);
      }
    } catch (error) {
      console.error("Error fetching guest:", error);
      setGuest(null);
    } finally {
      setIsGuestLoaded(true);
    }
  };

  const confirmAttendance = async (confirmed: number) => {
    if (!token || !guest) return;
    const docRef = doc(db, "guests", token);
    await updateDoc(docRef, {
      confirmed,
      hasConfirmed: true,
    });
    setGuest((prev) =>
      prev ? { ...prev, confirmed, hasConfirmed: true } : prev
    );
  };

  return (
    <GuestContext.Provider
      value={{ token, guest, isGuestLoaded, confirmAttendance }}
    >
      {children}
    </GuestContext.Provider>
  );
};
